import React, { useEffect, useCallback } from 'react';

/**
 * Full-screen image viewer (Escape / backdrop / close button).
 * @param {{ isOpen: boolean, src: string | null, alt?: string, onClose: () => void }} props
 */
const ImageLightbox = ({ isOpen, src, alt = '', onClose }) => {
  const handleKey = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    document.addEventListener('keydown', handleKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, handleKey]);

  if (!isOpen || !src) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/92 p-4 sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={alt || 'Image preview'}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-3 right-3 sm:top-6 sm:right-6 z-[101] rounded-full p-2 sm:p-3 bg-white/15 text-white hover:bg-white/25 transition-colors"
        aria-label="Close"
      >
        <span className="material-symbols-outlined text-2xl sm:text-3xl">close</span>
      </button>
      <img
        src={src}
        alt={alt}
        className="max-h-[min(90vh,94dvh)] max-w-full w-auto object-contain rounded-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
      {alt ? (
        <p className="mt-4 text-center text-sm sm:text-base text-white/90 max-w-2xl px-2">{alt}</p>
      ) : null}
    </div>
  );
};

export default ImageLightbox;
